import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import {
	CssBaseline,
	Drawer as MuiDrawer,
	Box,
	AppBar as MuiAppBar,
	Toolbar,
	List,
	Typography,
	Divider,
	IconButton,
	Badge,
	Container,
	Link,
	Tooltip,
} from '@mui/material';
import {
	Menu as MenuIcon,
	ChevronLeft as ChevronLeftIcon,
	Notifications as NotificationsIcon,
	Brightness4 as DarkModeIcon,
	Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import { MainMenuItems, secondaryListItems } from './components/MainMenuItems';
import { useLocation } from 'react-router-dom';

function Copyright(props) {
	return (
		<Typography variant="body2" color="text.secondary" align="center" {...props}>
			{'Copyright © '}
			<Link color="inherit" href="#">
				Horrol
			</Link>{' '}
			{new Date().getFullYear()}
			{'.'}
		</Typography>
	);
}

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
	shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
	zIndex: theme.zIndex.drawer + 1,
	transition: theme.transitions.create(['width', 'margin'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		marginLeft: drawerWidth,
		width: `calc(100% - ${drawerWidth}px)`,
		transition: theme.transitions.create(['width', 'margin'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
	({ theme, open }) => ({
		'& .MuiDrawer-paper': {
			position: 'relative',
			whiteSpace: 'nowrap',
			width: drawerWidth,
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
			boxSizing: 'border-box',
			...(!open && {
				overflowX: 'hidden',
				transition: theme.transitions.create('width', {
					easing: theme.transitions.easing.sharp,
					duration: theme.transitions.duration.leavingScreen,
				}),
				width: theme.spacing(7),
				[theme.breakpoints.up('sm')]: {
					width: theme.spacing(9),
				},
			}),
		},
	})
);

function MainComponent({ themeMode, toggleThemeMode }) {
	const [open, setOpen] = useState(false);
	const [containerSize, setContainerSize] = useState('lg');
	const toggleDrawer = () => {
		setOpen(!open);
	};

	let location = useLocation();
	useEffect(() => {
		const exceptionSizes = {
			'/character_template': 'false',
			'/character_template/[-\\w]+': 'false',
		};

		const matchingException = Object.keys(exceptionSizes).find((path) => {
			const regex = new RegExp(`^${path}$`);
			console.log('testing', location.pathname, regex.test(location.pathname));
			return regex.test(location.pathname);
		});

		if (matchingException) {
			console.log('matches exception for', location);
			setContainerSize(exceptionSizes[matchingException]);
		} else {
			setContainerSize('lg');
		}
	}, [location]);

	return (
		<Box sx={{ display: 'flex' }}>
			<CssBaseline />
			<AppBar position="absolute" open={open}>
				<Toolbar
					sx={{
						pr: '24px', // keep right padding when drawer closed
					}}>
					<IconButton
						edge="start"
						color="inherit"
						aria-label="open drawer"
						onClick={toggleDrawer}
						sx={{
							marginRight: '36px',
							...(open && { display: 'none' }),
						}}>
						<MenuIcon />
					</IconButton>
					<Typography
						component="h1"
						variant="h6"
						color="inherit"
						noWrap
						sx={{ flexGrow: 1 }}>
						Horrol {process.env.REACT_APP_API_ENV} {process.env.REACT_APP_TAG}
					</Typography>
					<IconButton color="inherit">
						<Badge badgeContent={4} color="secondary">
							<NotificationsIcon />
						</Badge>
					</IconButton>
					<Tooltip title={`Switch to ${themeMode === 'light' ? 'dark' : 'light'} mode`}>
						<IconButton color="inherit" onClick={toggleThemeMode}>
							{themeMode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
						</IconButton>
					</Tooltip>
				</Toolbar>
			</AppBar>
			<Drawer variant="permanent" open={open}>
				<Toolbar
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						px: [1],
					}}>
					<IconButton onClick={toggleDrawer}>
						<ChevronLeftIcon />
					</IconButton>
				</Toolbar>
				<Divider />
				<List component="nav">
					{MainMenuItems}
					{/* <Divider sx={{ my: 1 }} />
						{secondaryListItems} */}
				</List>
			</Drawer>
			<Box
				component="main"
				sx={{
					backgroundColor: (theme) =>
						theme.palette.mode === 'light'
							? theme.palette.grey[100]
							: theme.palette.grey[900],
					flexGrow: 1,
					height: '100vh',
					overflow: 'auto',
				}}>
				<Toolbar />
				<Container
					display="flex"
					flex-direction="column"
					maxWidth={containerSize}
					sx={{ pt: 4, height: 'calc(100% - 64px)' }}>
					<Box sx={{ minHeight: '97%' }}>
						<Outlet />
					</Box>
					<Copyright sx={{ height: '3%' }} />
				</Container>
			</Box>
		</Box>
	);
}

export default function Main(props) {
	return <MainComponent {...props} />;
}
